import { useEffect, useMemo, useRef, useState } from 'react';
import { calculateTimeLeft, EMPTY_TIME, TimeLeft } from '~/shared/utils/calculate-time-left';
import { trustedTime } from '~/shared/utils/trusted-time';

export type UseCountdownOptions = {
    endDate: string | Date;
    onExpire?: () => void;
    /** Avoid SSR/client mismatch by keeping zeros until mount. Default: true. */
    hydrateSafe?: boolean;
    /**
     * Stops the ticking without unmounting, for a countdown that is still in the document but
     * out of sight.
     */
    isPaused?: boolean;
};

export type UseCountdownResult = {
    timeLeft: TimeLeft;
    isExpired: boolean;
    hasMounted: boolean;
    endDate: Date;
};

export const useCountdown = ({
    endDate,
    onExpire,
    hydrateSafe = true,
    isPaused = false,
}: UseCountdownOptions): UseCountdownResult => {
    const end = useMemo(() => new Date(endDate), [endDate]);
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
        hydrateSafe ? EMPTY_TIME : calculateTimeLeft(end, trustedTime.now()),
    );
    const [hasMounted, setHasMounted] = useState(!hydrateSafe);
    const [isExpired, setIsExpired] = useState(false);
    const expiredRef = useRef(false);

    // Tied to the target alone: pausing and resuming must not let an expiry announce itself twice.
    useEffect(() => {
        expiredRef.current = false;
        setIsExpired(false);
    }, [end]);

    useEffect(() => {
        setHasMounted(true);

        if (isPaused) return;

        const expire = () => {
            if (expiredRef.current) return;
            expiredRef.current = true;
            setIsExpired(true);
            setTimeLeft(EMPTY_TIME);
            onExpireRef.current?.();
        };

        const tick = () => {
            const now = trustedTime.now();

            setTimeLeft(calculateTimeLeft(end, now));

            if (end.getTime() - now <= 0) {
                expire();
                return false;
            }

            return true;
        };

        if (!tick()) return;

        let interval: ReturnType<typeof setInterval> | undefined;

        /*
         * Aligned to the wall clock rather than counting 1000ms from mount, so two countdowns
         * mounted at different moments land on the same second and never show the viewer two
         * different numbers at once.
         */
        const alignment = setTimeout(
            () => {
                if (!tick()) return;

                interval = setInterval(() => {
                    if (!tick() && interval) clearInterval(interval);
                }, 1000);
            },
            1000 - (trustedTime.now() % 1000),
        );

        return () => {
            clearTimeout(alignment);

            if (interval) clearInterval(interval);
        };
    }, [end, isPaused]);

    return { timeLeft, isExpired, hasMounted, endDate: end };
};
